import {
  Flex,
  Heading,
  SimpleGrid,
  Spacer,
  Button,
  Box,
  useToast,
  VStack,
  Checkbox,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Tfoot,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Text,
  Badge,
} from '@chakra-ui/react';
import { Api } from 'api';
import { CitySelect, CountySelect, FormControlFormPhoneKvKKInput, LoadingIndicator } from 'components';
import {
  DEFAULT_FORM_SIMPLE_GRID_LAYOUT,
  FormControlFormGovIdInput,
  FormControlFormInput,
  FormControlFormNameInput,
  FormControlFormPhoneInput,
  GenericMessages,
  govIdCharacterLengthRegex,
  OnlyLetterRegex,
  yupGovIdFirstCharCheck,
  yupNameFieldCheck,
  yupNameFieldWhiteSpaceCheck,
  createCustomerSocket,
} from 'core';
import { refetchCustomer, setCustomerFormDirty } from 'features';
import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { AvailableUpgradePackage, Customer, CustomerQueryCacheKey, CustomerResponse } from 'types';
import * as yup from 'yup';
import { Socket } from 'socket.io-client';
import { t, Trans } from '@lingui/macro';

type FormData = {
  clientInfo: {
    name: string;
    firmName?: string;
    mersisNo?: string;
    authorizationNo?: string;
    phone: string;
    city?: string;
    county?: string;
    govId: string;
    email: string;
    address?: string;
    isIndividual: boolean;
  };
  carOwnerInfo: {
    name?: string;
    phone?: string;
    isDifferent?: boolean;
    city?: string;
    county?: string;
    govId?: string;
    email?: string;
    address?: string;
    isIndividual: boolean;
    firmName?: string;
  };
  // expertisePackage?: string;
  // expertisePackageGroup?: string;
};

const formValidation = yup.object({
  clientInfo: yup.object({
    name: yup
      .string()
      .required(GenericMessages.CannotBeEmpty)
      .matches(OnlyLetterRegex, GenericMessages.OnlyLetter)
      .test('checkNameWhiteSpace', GenericMessages.NameWhiteSpaceBetweenWords, yupNameFieldWhiteSpaceCheck)
      .test('checkNameWords', GenericMessages.NameMinTwoLetters, yupNameFieldCheck),
    firmName: yup.string(),
    mersisNo: yup.string().when('isIndividual', {
      is: false, // When isIndividual is false
      then: yup.string().required(GenericMessages.CannotBeEmpty),
    }),
    authorizationNo: yup.string().when('isIndividual', {
      is: false, // When isIndividual is false
      then: yup.string().required(GenericMessages.CannotBeEmpty),
    }),
    phone: yup.string().required(GenericMessages.CannotBeEmpty),
    city: yup.number().required(GenericMessages.CannotBeEmpty),
    county: yup.number().required(GenericMessages.CannotBeEmpty),
    govId: yup
      .string()
      .required(GenericMessages.CannotBeEmpty)
      .when('clientInfo.isIndividual', {
        is: true,
        then: yup
          .string()
          .required(GenericMessages.CannotBeEmpty)
          .test('checkGovIdFirstChar', GenericMessages.CannotStartWithZero, yupGovIdFirstCharCheck)
          .matches(govIdCharacterLengthRegex, GenericMessages.InvalidGovId),
      }),
    email: yup.string().nullable().email(GenericMessages.InvalidEmail),
    address: yup.string().nullable(),
    isIndividual: yup.bool(),
  }),
  carOwnerInfo: yup.object({
    name: yup.string().when('.', {
      is: (val: string) => val?.length > 0,
      then: yup
        .string()
        .matches(OnlyLetterRegex, GenericMessages.OnlyLetter)
        .test('checkCarOwnerNameWhiteSpace', GenericMessages.NameWhiteSpaceBetweenWords, yupNameFieldWhiteSpaceCheck)
        .test('checkCarOwnerNameWords', GenericMessages.NameMinTwoLetters, yupNameFieldCheck),
    }),
    phone: yup.string(),
    isDifferent: yup.bool(),
    city: yup.number(),
    county: yup.number(),
    govId: yup.string().when('carOwnerInfo.isIndividual', {
      is: true,
      then: yup
        .string()
        .required(GenericMessages.CannotBeEmpty)
        .test('checkGovIdFirstChar', GenericMessages.CannotStartWithZero, yupGovIdFirstCharCheck)
        .matches(govIdCharacterLengthRegex, GenericMessages.InvalidGovId),
    }),
    email: yup.string().nullable().email(GenericMessages.InvalidEmail),
    address: yup.string().nullable(),
    isIndividual: yup.bool(),
    firmName: yup.string(),
  }),
  // expertisePackage: yup.number().required(GenericMessages.CannotBeEmpty),
  // expertisePackageGroup: yup.number().required(GenericMessages.CannotBeEmpty),
});

export const ExpertiseClientInformationPage = () => {
  const customer = useSelector((state: RootState) => state.customer.focusedCustomer);
  const canCustomerContinue = useSelector((state: RootState) => state.customer.canCustomerContinue);
  const campaignApplied = useSelector((state: RootState) => state.customer.isAppliedDiscountByCampaign);
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [hasKvkkApproval, setHasKvkkApproval] = useState(customer?.hasKvkkApproval || false);
  const [hasIsemriApproval, setHasIsemriApproval] = useState(customer?.hasIsemriApproval || false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customerSocket, setCustomerSocket] = useState<Socket | null>(null);

  // Package upgrade state
  const { isOpen: isUpgradeModalOpen, onOpen: onUpgradeModalOpen, onClose: onUpgradeModalClose } = useDisclosure();
  const {
    isOpen: isUpgradeConfirmOpen,
    onOpen: onUpgradeConfirmOpen,
    onClose: onUpgradeConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [selectedPackage, setSelectedPackage] = useState<AvailableUpgradePackage | null>(null);

  useEffect(() => {
    setHasKvkkApproval(customer?.hasKvkkApproval || false);
  }, [customer?.hasKvkkApproval]);

  useEffect(() => {
    setHasIsemriApproval(customer?.hasIsemriApproval || false);
  }, [customer?.hasIsemriApproval]);

  // const mutate = useMutation<string, any, FormData>(
  //   async (formData: FormData) => {
  //     await Api.Customer.saveCustomerInfo({
  //       id: customer!.id,
  //       name: formData.clientInfo.name,
  //       phoneNumber: formData.clientInfo.phone,
  //       countyId: formData.clientInfo.province,
  //       cityId: formData.clientInfo.district,
  //       governmentId: formData.clientInfo.govId,
  //       isIndividual: true,
  //       carLicencePhoneNumber: formData.carOwnerInfo.phone,
  //       carLicenceName: formData.carOwnerInfo.name,
  //       expertisePackageId: formData.expertisePackage,
  //     });
  //     return '';
  //   },
  //   {
  //     onSuccess: {
  //       // Do Nothing
  //     },
  //   },
  // );§

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const mutation = useMutation<void, any, FormData>(
    async (formData: FormData) => {
      await Api.Customer.saveCustomerInfo({
        id: customer!.id,
        name: formData.clientInfo.name,
        firmName: formData.clientInfo.firmName!,
        mersisNo: formData.clientInfo.mersisNo!,
        authorizationNo: formData.clientInfo.authorizationNo!,
        phoneNumber: formData.clientInfo.phone,
        countyId: parseInt(formData.clientInfo.county!, 10),
        cityId: parseInt(formData.clientInfo.city!, 10),
        governmentId: formData.clientInfo.govId,
        isIndividual: formData.clientInfo.isIndividual,
        carLicencePhoneNumber: formData.carOwnerInfo.phone,
        carLicenceName: formData.carOwnerInfo.name,
        carLicenceCityId: parseInt(formData.carOwnerInfo.city!, 10),
        carLicenceCountyId: parseInt(formData.carOwnerInfo.county!, 10),
        carLicenceGovernmentId: formData.carOwnerInfo.govId,
        carLicenceEmail: formData.carOwnerInfo.email,
        carLicenceAddress: formData.carOwnerInfo.address,
        carLicenceIsIndividual: formData.carOwnerInfo.isIndividual,
        carLicenceFirmName: formData.carOwnerInfo.firmName,

        // expertisePackageId: parseInt(formData.expertisePackage!, 10),
        // expertisePackageGroupId: parseInt(formData.expertisePackageGroup!, 10),
        email: formData.clientInfo.email,
        address: formData.clientInfo.address ? formData.clientInfo.address : '',
      });
    },
    {
      onSuccess: () => {
        dispatch(setCustomerFormDirty(false));
        dispatch(refetchCustomer(customer!.id));
        toast({
          title: 'Müşteri bilgileri başarılı bir şekilde kayıt edildi',
          status: 'success',
          isClosable: true,
        });
      },
      onError: (error) => {
        if (!customer?.hasKvkkApproval) {
          setHasKvkkApproval(false);
        }
        if (!customer?.hasIsemriApproval) {
          setHasIsemriApproval(false);
        }
        toast({
          title: error?.response?.data?.message || 'Müşteri bilgileri kayıt edilirken hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: formValidation,
    initialValues: {
      clientInfo: {
        name: customer?.name || '',
        firmName: customer?.firmName || '',
        mersisNo: customer?.mersisNo || '',
        authorizationNo: customer?.authorizationNo || '',
        phone: customer?.phoneNumber || '',
        county: customer?.countyId ? customer?.countyId.toString() : '',
        city: customer?.cityId ? customer?.cityId.toString() : '',
        govId: customer?.governmentId || '',
        email: customer?.email || '',
        address: customer?.address || '',
        isIndividual: customer!.isIndividual,
      },
      carOwnerInfo: {
        name: customer?.carLicenceName || '',
        phone: customer?.carLicencePhoneNumber || '',
        isDifferent: Boolean(
          customer?.carLicenceName ||
            customer?.carLicencePhoneNumber ||
            customer?.carLicenceCountyId ||
            customer?.carLicenceCityId ||
            customer?.carLicenceGovernmentId ||
            customer?.carLicenceEmail,
        ),
        city: customer?.carLicenceCityId ? customer?.carLicenceCityId.toString() : '',
        county: customer?.carLicenceCountyId ? customer?.carLicenceCountyId.toString() : '',
        govId: customer?.carLicenceGovernmentId || '',
        email: customer?.carLicenceEmail || '',
        address: customer?.carLicenceAddress || '',
        isIndividual: customer!.carLicenceIsIndividual,
        firmName: customer?.carLicenceFirmName || '',
      },
      // expertisePackage: customer?.expertisePackageId ? customer?.expertisePackageId.toString() : '',
      // expertisePackageGroup: customer?.expertisePackageGroupId ? customer?.expertisePackageGroupId.toString() : '',
    },
    onSubmit: (data: FormData) => {
      mutation.mutateAsync(data);
    },
  });

  const { handleChange, values, errors, dirty, setFieldValue, resetForm } = formik;

  useEffect(() => {
    dispatch(setCustomerFormDirty(dirty));
  }, [dirty]);

  useEffect(() => {
    if (customer) {
      const newCustomerSocket = createCustomerSocket(customer.id.toString());
      setCustomerSocket(newCustomerSocket);

      newCustomerSocket.on('customerChanged', (data: CustomerResponse) => {
        if (data.customer?.id === customer?.id) {
          dispatch(refetchCustomer(data.customer?.id));
        }
      });

      return () => {
        newCustomerSocket.disconnect();
      };
    }
    return undefined;
  }, [customer, dispatch]);

  const { data: customerPackageInfo, isLoading: isCustomerPackageInfoLoading } = useQuery(
    [CustomerQueryCacheKey.GetCustomerPackageInfo, customer?.id],
    async () => {
      const response = await Api.Customer.getCustomerPackageInfo(customer!.id);
      return response.data;
    },
    {
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  // Package upgrade queries & mutation
  const { data: upgradePackages } = useQuery(
    [CustomerQueryCacheKey.GetAvailableUpgradePackages, customer?.id],
    async () => {
      const response = await Api.Customer.getAvailableUpgradePackages(customer!.id);
      return response.data;
    },
    {
      enabled: customer != null,
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  );

  const upgradePackageMutation = useMutation(
    async (newExpertisePackageId: number) => {
      const response = await Api.Customer.upgradePackage(customer!.id, { newExpertisePackageId });
      return response.data;
    },
    {
      onSuccess: () => {
        toast({
          title: 'Paket yükseltme işlemi başarılı',
          status: 'success',
          isClosable: true,
        });
        onUpgradeConfirmClose();
        onUpgradeModalClose();
        setSelectedPackage(null);
        dispatch(refetchCustomer(customer!.id));
        queryClient.invalidateQueries([CustomerQueryCacheKey.GetCustomerPackageInfo, customer?.id]);
        queryClient.invalidateQueries([CustomerQueryCacheKey.GetAvailableUpgradePackages, customer?.id]);
        queryClient.invalidateQueries([CustomerQueryCacheKey.GetCustomerAbilities, customer?.id]);
      },
      onError: (error: any) => {
        toast({
          title: error?.response?.data?.message || 'Paket yükseltme sırasında hata oluştu',
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const handleSelectPackage = (pkg: AvailableUpgradePackage) => {
    setSelectedPackage(pkg);
    onUpgradeConfirmOpen();
  };

  const handleConfirmUpgrade = () => {
    if (selectedPackage) {
      upgradePackageMutation.mutate(selectedPackage.id);
    }
  };

  const onKvKKConfirmation = (kvKKCustomerResponse?: Customer) => {
    if (kvKKCustomerResponse) {
      const { name, governmentId, countyId, cityId, email, address, firmName } = kvKKCustomerResponse;
      setFieldValue('clientInfo.name', name);
      setFieldValue('clientInfo.govId', governmentId);
      setFieldValue('clientInfo.county', countyId);
      setFieldValue('clientInfo.city', cityId);
      setFieldValue('clientInfo.email', email || '');
      setFieldValue('clientInfo.firmName', firmName);
      setFieldValue('clientInfo.address', address || '');
      setHasKvkkApproval(true);
      setHasIsemriApproval(true);
    } else {
      setHasKvkkApproval(true);
      setHasIsemriApproval(true);
    }
  };

  useEffect(() => {
    if (!customer?.countyId) {
      setFieldValue('clientInfo.county', '');
    }

    if (!customer?.cityId) {
      setFieldValue('clientInfo.city', '');
    }

    if (!customer?.expertisePackageId) {
      setFieldValue('expertisePackage', '');
    }

    resetForm({ values });
  }, [customer?.id]);

  if (!customer) {
    return null;
  }

  return (
    <Flex align="stretch" direction="column" basis="100%" gap="2">
      <Heading as="h4" size="md">
        <Trans>Müşteri Bilgileri</Trans>
      </Heading>
      <Spacer />
      {/* <Box>
        <Button>Occo Kartı Tara</Button>
      </Box>
      <Spacer /> */}
      <form onSubmit={formik.handleSubmit} noValidate>
        <Flex align="stretch" direction="column" basis="100%" gap="2">
          <SimpleGrid spacing="30px" columns={DEFAULT_FORM_SIMPLE_GRID_LAYOUT}>
            <VStack align="self-start">
              <FormControlFormNameInput
                label={t`Ad Soyad`}
                name="clientInfo.name"
                onChange={handleChange}
                value={values.clientInfo.name}
                error={errors.clientInfo?.name != null}
                helperText={errors.clientInfo?.name}
                isDisabled={!canCustomerContinue}
              />
              <Checkbox
                borderColor="blue.200"
                colorScheme="blue"
                name="clientInfo.isIndividual"
                onChange={(event) => {
                  setFieldValue(event.target.name, !event.target.checked);
                }}
                isChecked={!values.clientInfo.isIndividual}
                isDisabled={!canCustomerContinue}
              >
                <Trans>Kurumsal</Trans>
              </Checkbox>
            </VStack>
            {!values.clientInfo.isIndividual && (
              <FormControlFormInput
                label={t`Firma Adı`}
                name="clientInfo.firmName"
                onChange={handleChange}
                value={values.clientInfo.firmName}
                error={errors.clientInfo?.firmName != null}
                helperText={errors.clientInfo?.firmName}
                isDisabled={!canCustomerContinue}
              />
            )}
            {!values.clientInfo.isIndividual && (
              <FormControlFormInput
                label={t`Mersis No`}
                name="clientInfo.mersisNo"
                onChange={handleChange}
                value={values.clientInfo.mersisNo}
                error={errors.clientInfo?.mersisNo != null}
                helperText={errors.clientInfo?.mersisNo}
                isDisabled={!canCustomerContinue}
              />
            )}
            {!values.clientInfo.isIndividual && (
              <FormControlFormInput
                label={t`Yetki Belge Numarası`}
                name="clientInfo.authorizationNo"
                onChange={handleChange}
                value={values.clientInfo.authorizationNo}
                error={errors.clientInfo?.authorizationNo != null}
                helperText={errors.clientInfo?.authorizationNo}
                isDisabled={!canCustomerContinue}
              />
            )}
            <FormControlFormPhoneKvKKInput
              label={t`Telefon`}
              name="clientInfo.phone"
              onChange={handleChange}
              value={values.clientInfo.phone}
              error={errors.clientInfo?.phone != null}
              helperText={errors.clientInfo?.phone}
              type="text"
              isDisabled={!canCustomerContinue || campaignApplied}
              onKvKKConfirmation={onKvKKConfirmation}
              showKvKKButton={
                (!hasKvkkApproval || !hasIsemriApproval) && !!values.clientInfo.name && !!values.clientInfo.phone
              }
              customerName={values.clientInfo.name}
            />
            <CitySelect
              name="clientInfo.city"
              onChange={handleChange}
              value={values.clientInfo.city}
              error={errors.clientInfo?.city != null}
              helperText={errors.clientInfo?.city}
              isDisabled={!canCustomerContinue}
            />
            <CountySelect
              cityId={values.clientInfo?.city ? parseInt(values.clientInfo?.city, 10) : undefined}
              name="clientInfo.county"
              onChange={handleChange}
              value={values.clientInfo.county}
              error={errors.clientInfo?.county != null}
              helperText={errors.clientInfo?.county}
              isDisabled={!canCustomerContinue}
            />
            <FormControlFormGovIdInput
              label={values.clientInfo.isIndividual ? t`Tc Kimlik ` : t`Vergi Numarası`}
              name="clientInfo.govId"
              value={values.clientInfo.govId}
              error={errors.clientInfo?.govId != null}
              helperText={errors.clientInfo?.govId}
              onChange={handleChange}
              type="text"
              isDisabled={!canCustomerContinue}
              isIndividual={values.clientInfo.isIndividual}
            />
            <FormControlFormInput
              label={t`E-Posta Adresi`}
              name="clientInfo.email"
              onChange={handleChange}
              value={values.clientInfo.email}
              error={errors.clientInfo?.email != null}
              helperText={errors.clientInfo?.email}
              type="email"
              isDisabled={!canCustomerContinue}
            />
            <FormControlFormInput
              label={t`Adres`}
              name="clientInfo.address"
              onChange={handleChange}
              value={values.clientInfo.address}
              error={errors.clientInfo?.address != null}
              helperText={errors.clientInfo?.address}
              type="text"
              isDisabled={!canCustomerContinue}
            />
          </SimpleGrid>
          <Spacer />
          <Flex align="center" gap="2">
            <Heading as="h4" size="md">
              <Trans>Araç Sahibi Bilgileri</Trans>
            </Heading>
            <Checkbox
              borderColor="blue.200"
              colorScheme="blue"
              name="carOwnerInfo.isDifferent"
              onChange={(event) => {
                setFieldValue('carOwnerInfo.isDifferent', event.target.checked);
              }}
              isChecked={values.carOwnerInfo.isDifferent}
            >
              <Trans>Müşteri Bilgilerinden Farklı</Trans>
            </Checkbox>
          </Flex>
          <Spacer />
          <SimpleGrid spacing="30px" columns={DEFAULT_FORM_SIMPLE_GRID_LAYOUT}>
            {values.carOwnerInfo.isDifferent && (
              <>
                <FormControlFormNameInput
                  label={t`Ad`}
                  name="carOwnerInfo.name"
                  value={values.carOwnerInfo.name}
                  error={errors.carOwnerInfo?.name != null}
                  helperText={errors.carOwnerInfo?.name}
                  isDisabled={!canCustomerContinue}
                  onChange={handleChange}
                />
                <FormControlFormPhoneInput
                  label={t`Telefon`}
                  name="carOwnerInfo.phone"
                  onChange={handleChange}
                  value={values.carOwnerInfo.phone}
                  error={errors.carOwnerInfo?.phone != null}
                  helperText={errors.carOwnerInfo?.phone}
                  isDisabled={!canCustomerContinue}
                  type="text"
                />
                <CitySelect
                  name="carOwnerInfo.city"
                  onChange={handleChange}
                  value={values.carOwnerInfo.city}
                  error={errors.carOwnerInfo?.city != null}
                  helperText={errors.carOwnerInfo?.city}
                  isDisabled={!canCustomerContinue}
                />
                <CountySelect
                  cityId={values.carOwnerInfo?.city ? parseInt(values.carOwnerInfo?.city, 10) : undefined}
                  name="carOwnerInfo.county"
                  onChange={handleChange}
                  value={values.carOwnerInfo.county}
                  error={errors.carOwnerInfo?.county != null}
                  helperText={errors.carOwnerInfo?.county}
                  isDisabled={!canCustomerContinue}
                />
                <FormControlFormGovIdInput
                  label={values.carOwnerInfo.isIndividual ? t`Tc Kimlik ` : t`Vergi Numarası`}
                  name="carOwnerInfo.govId"
                  value={values.carOwnerInfo.govId}
                  error={errors.carOwnerInfo?.govId != null}
                  helperText={errors.carOwnerInfo?.govId}
                  onChange={handleChange}
                  isDisabled={!canCustomerContinue}
                  type="text"
                  isIndividual={values.carOwnerInfo.isIndividual}
                />
                <FormControlFormInput
                  label={t`E-Posta Adresi`}
                  name="carOwnerInfo.email"
                  onChange={handleChange}
                  value={values.carOwnerInfo.email}
                  error={errors.carOwnerInfo?.email != null}
                  helperText={errors.carOwnerInfo?.email}
                  type="email"
                  isDisabled={!canCustomerContinue}
                />
                <FormControlFormInput
                  label={t`Adres`}
                  name="carOwnerInfo.address"
                  onChange={handleChange}
                  value={values.carOwnerInfo.address}
                  error={errors.carOwnerInfo?.address != null}
                  helperText={errors.carOwnerInfo?.address}
                  type="text"
                />
                {!values.carOwnerInfo.isIndividual && (
                  <FormControlFormInput
                    label={t`Firma Adı`}
                    name="carOwnerInfo.firmName"
                    onChange={handleChange}
                    value={values.carOwnerInfo.firmName}
                    error={errors.carOwnerInfo?.firmName != null}
                    helperText={errors.carOwnerInfo?.firmName}
                    isDisabled={!canCustomerContinue}
                  />
                )}
                <Checkbox
                  borderColor="blue.200"
                  colorScheme="blue"
                  name="carOwnerInfo.isIndividual"
                  onChange={(event) => {
                    setFieldValue(event.target.name, !event.target.checked);
                    if (!event.target.checked) {
                      setFieldValue('carOwnerInfo.firmName', '');
                    }
                  }}
                  isChecked={!values.carOwnerInfo.isIndividual}
                  isDisabled={!canCustomerContinue}
                >
                  <Trans>Kurumsal</Trans>
                </Checkbox>
              </>
            )}
          </SimpleGrid>
          <Spacer />
          <Heading as="h4" size="md">
            <Trans>Ekspertiz Hizmet Paketi</Trans>
          </Heading>
          <Spacer />
          {isCustomerPackageInfoLoading || !customerPackageInfo ? (
            <LoadingIndicator />
          ) : (
            <Box maxW="420px" mb={4} border="1px solid #83c39e" borderRadius="lg" overflow="hidden" boxShadow="md">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr bg="blue.400">
                    <Th fontWeight="bold" color="white">
                      Hizmet İçeriği
                    </Th>
                    <Th isNumeric fontWeight="bold" color="white">
                      Tutar (₺)
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td>{customerPackageInfo.packageInfo?.name || 'Paket'}</Td>
                    <Td isNumeric>{customerPackageInfo.packageInfo?.price?.toLocaleString('tr-TR')}</Td>
                  </Tr>
                  {customerPackageInfo.additionalServices?.map((service: any) => (
                    <Tr key={service.name}>
                      <Td>{service.name}</Td>
                      <Td isNumeric>{service.price?.toLocaleString('tr-TR')}</Td>
                    </Tr>
                  ))}
                </Tbody>
                <Tfoot>
                  <Tr bg="blue.400">
                    <Th fontWeight={800} color="white">
                      Toplam Liste Bedeli
                    </Th>
                    <Th isNumeric fontWeight={800} color="white">
                      {customerPackageInfo.priceWithoutDiscount?.toLocaleString('tr-TR')}
                    </Th>
                  </Tr>
                  <Tr bg="blue.400">
                    <Th fontWeight={800} color="white">
                      Özel İndirim
                    </Th>
                    <Th isNumeric fontWeight={800} color="red">
                      -{customerPackageInfo.discount?.toLocaleString('tr-TR')}
                    </Th>
                  </Tr>
                  <Tr bg="blue.400">
                    <Th fontSize="lg" color="white">
                      Net Ödeme Tutarı
                    </Th>
                    <Th isNumeric fontSize="lg" color="green.700">
                      {customerPackageInfo.finalPrice?.toLocaleString('tr-TR')}
                    </Th>
                  </Tr>
                </Tfoot>
              </Table>
            </Box>
          )}
          {upgradePackages && upgradePackages.length > 0 && canCustomerContinue && (
            <Button colorScheme="orange" size="md" maxW="420px" onClick={onUpgradeModalOpen}>
              <Trans>Paket Değiştir</Trans>
            </Button>
          )}

          {/* Package Upgrade Modal */}
          <Modal isOpen={isUpgradeModalOpen} onClose={onUpgradeModalClose} isCentered size="lg">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Trans>Paket Yükseltme</Trans>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text fontSize="sm" color="gray.400" mb={4}>
                  <Trans>
                    Mevcut paketinizi aşağıdaki paketlerden birine yükseltebilirsiniz. Fark tutarı bakiyenizden
                    düşülecektir.
                  </Trans>
                </Text>
                <Flex direction="column" gap={3}>
                  {upgradePackages?.map((pkg) => (
                    <Box
                      key={pkg.id}
                      p={4}
                      border="2px solid"
                      borderColor="gray.300"
                      borderRadius="lg"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      bg="white"
                      _hover={{ bg: 'gray.100', borderColor: 'gray.600', transform: 'scale(1.01)' }}
                      _active={{ bg: 'gray.200', borderColor: 'gray.700' }}
                      transition="all 0.15s ease"
                      cursor="pointer"
                      onClick={() => handleSelectPackage(pkg)}
                    >
                      <Box>
                        <Text fontWeight="bold" color="gray.800">
                          {pkg.name}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {t`Fiyat`}: {pkg.price?.toLocaleString('tr-TR')} ₺
                        </Text>
                      </Box>
                      <Badge bg="gray.700" color="white" fontSize="md" px={3} py={1} borderRadius="md">
                        {t`Fark`}: {pkg.priceDifference?.toLocaleString('tr-TR')} ₺
                      </Badge>
                    </Box>
                  ))}
                </Flex>
              </ModalBody>
              <ModalFooter>
                <Button onClick={onUpgradeModalClose}>
                  <Trans>Kapat</Trans>
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Upgrade Confirmation Dialog */}
          <AlertDialog
            isOpen={isUpgradeConfirmOpen}
            leastDestructiveRef={cancelRef as React.RefObject<HTMLButtonElement>}
            onClose={onUpgradeConfirmClose}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  <Trans>Paket Yükseltme Onayı</Trans>
                </AlertDialogHeader>
                <AlertDialogBody>
                  {selectedPackage && (
                    <Text>
                      {selectedPackage.name} {t`paketine geçiş yapılacak. Bakiyenizden`}{' '}
                      {selectedPackage.royaltyAmount?.toLocaleString('tr-TR')} ₺ {t`düşülecektir. Onaylıyor musunuz?`}
                    </Text>
                  )}
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onUpgradeConfirmClose}>
                    <Trans>İptal</Trans>
                  </Button>
                  <Button
                    colorScheme="orange"
                    onClick={handleConfirmUpgrade}
                    ml={3}
                    isLoading={upgradePackageMutation.isLoading}
                  >
                    <Trans>Onayla</Trans>
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
          {canCustomerContinue && (
            <Box width="100%" display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                isLoading={mutation.isLoading}
                border={customer?.phoneNumber ? '2px solid green' : 'none'}
              >
                <Trans>Kaydet</Trans>
              </Button>
            </Box>
          )}
        </Flex>
      </form>
    </Flex>
  );
};
